import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Advanced, Badge, Btn } from "./common";
import { Lock, Link2, Unlink, RefreshCw, Send, Archive } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const REASONS = {
  connected: ["success", "Gmail yetkilendirmesi güncellendi."],
  denied: ["error", "Google izin ekranında onay verilmedi."],
  wrong_account: ["error", "Yanlış hesap: yalnızca emre@modihome.co.uk kabul edilir. Bağlantı kurulmadı."],
  scope_too_wide: ["error", "Yalnızca okuma izni kabul edilir; verilen izin fazla olduğu için bağlanmadı."],
  scope_missing: ["error", "Okuma izni (gmail.readonly) verilmedi; bağlanmadı."],
  state_invalid: ["error", "Güvenlik doğrulaması geçersiz (state); işlem iptal edildi."],
  state_expired: ["error", "Yetkilendirme süresi doldu; tekrar deneyin."],
  exchange_failed: ["error", "Google kod değişimi başarısız; tekrar deneyin."],
  identity_failed: ["error", "Hesap kimliği doğrulanamadı; bağlanmadı."],
  not_configured: ["error", "Sunucuda Gmail yapılandırması eksik."],
  bad_request: ["error", "Geri dönüş isteği eksik/bozuk; işlem iptal edildi."],
};

/* Mailbox connection (READ ONLY). Separate from the app sign-in; nothing here claims "connected"
   unless the server really holds a working, owner-matched Gmail authorisation. */
export const GmailConnectionCard = ({ onChanged }) => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/gmail/status"); setS(r.data); onChanged?.(r.data); }
    catch (e) { setS({ error: true }); }
  }, [onChanged]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("gmail");
    if (!p) return;
    const [kind, msg] = REASONS[p] || ["error", `Gmail (${p}).`];
    toast[kind === "success" ? "success" : "error"](tx(msg));
    window.history.replaceState({}, "", window.location.pathname);
    load();
  }, [load, tx]);

  const connect = async (mode = "read") => {
    setBusy(true);
    try {
      const r = await api.post("/gmail/connect", null, { params: { mode } });
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || tx("Bağlantı başlatılamadı"));
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm(tx("Gmail bağlantısı kesilsin mi? Google'daki okuma izni de iptal edilir ve yeniden bağlanmak için izin ekranından geçmeniz gerekir."))) return;
    setBusy(true);
    try {
      const r = await api.post("/gmail/disconnect");
      toast.success(r.data.revoked_at_google ? tx("Bağlantı kaldırıldı ve Google'da iptal edildi") : tx("Bağlantı kaldırıldı"));
      load();
    } catch (e) { toast.error(tx("Bağlantı kaldırılamadı")); } finally { setBusy(false); }
  };

  if (!s) return null;
  const connected = !!s.connected;
  const ready = !!s.config_ready;

  return (
    <div data-testid="gmail-connection-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <Badge color={connected ? "green" : ready ? "amber" : "gray"} testid="gmail-connection-badge">
          {connected ? (s.can_send ? tx("Bağlı · gönderme açık") : s.can_modify ? tx("Bağlı · arşiv/çöp açık") : tx("Bağlı · yalnız okuma")) : ready ? tx("Bağlı değil") : tx("Sunucu yapılandırması eksik")}
        </Badge>
        <div className="flex gap-2">
          {connected ? (
            <>
              {!s.can_send && (
                <Btn onClick={() => connect("send")} disabled={busy} data-testid="gmail-enable-send-btn">
                  <Send size={13} className="inline mr-1" />{tx("Göndermeyi etkinleştir")}
                </Btn>
              )}
              {!s.can_modify && (
                <Btn onClick={() => connect("modify")} disabled={busy} data-testid="gmail-enable-modify-btn">
                  <Archive size={13} className="inline mr-1" />{tx("Arşiv/Çöp'ü etkinleştir")}
                </Btn>
              )}
              <Btn variant="ghost" onClick={load} data-testid="gmail-refresh-btn"><RefreshCw size={13} className="inline mr-1" />{tx("Yenile")}</Btn>
              <Btn variant="danger" onClick={disconnect} disabled={busy} data-testid="gmail-disconnect-btn">
                <Unlink size={13} className="inline mr-1" />{tx("Bağlantıyı kes")}
              </Btn>
            </>
          ) : (
            <Btn onClick={() => connect("read")} disabled={busy || !ready} data-testid="gmail-connect-btn">
              <Link2 size={13} className="inline mr-1" />{busy ? tx("Yönlendiriliyor…") : tx("Google ile bağla")}
            </Btn>
          )}
        </div>
      </div>

      {connected ? (
        <div className="text-sm text-white/80" data-testid="gmail-summary">
          <div className="text-white">{s.mailbox}</div>
          <div className="text-[0.72rem] text-white/50 mt-0.5">
            {tx("Mail okuma bağlı")} • {s.can_send ? tx("Gönderme açık") : tx("Gönderme kapalı")} • {s.can_modify ? tx("Arşiv/Çöp açık") : tx("Arşiv/Çöp kapalı")}
          </div>
        </div>
      ) : (
        <div className="text-sm text-white/70 flex items-start gap-2" data-testid="gmail-login-vs-mailbox">
          <Lock size={13} className="text-amber-300 mt-0.5 shrink-0" />
          <span>{tx("Google ile uygulama girişi posta kutusu erişimi değildir. Yalnızca okuma izni istenir ve sadece bu hesap kabul edilir:")}
            <code className="text-cyan-300"> {s.allowed_mailbox}</code></span>
        </div>
      )}

      <Advanced label={tx("İzinler ve bağlantı ayrıntıları")} testid="gmail-advanced">
        <div>{tx("Gönderme (gmail.send) ve arşiv/çöp (gmail.modify) ayrı izinlerdir; yalnızca siz etkinleştirirseniz istenir. Kalıcı silme izni hiç istenmez — çöp işlemi geri alınabilir.")}</div>
        <div>{tx("İzin verilen posta kutusu")}: {s.allowed_mailbox}</div>
        {connected && (
          <div data-testid="gmail-connection-detail">
            {s.connected_at} · {s.token_expires_at} · {s.has_refresh_token ? tx("var") : tx("yok")} · {s.last_read_at || "—"}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(s.config || {}).map(([k, v]) => (
            <Badge key={k} color={v ? "green" : "gray"} testid={`gmail-env-${k}`}>{k.replace("GOOGLE_OAUTH_", "")}: {v ? tx("var") : tx("yok")}</Badge>
          ))}
        </div>
        <div>{tx("Anahtar/secret değerleri hiçbir ekranda gösterilmez; yalnızca sunucuda var/yok bilgisi okunur.")}</div>
      </Advanced>
    </div>
  );
};

export default GmailConnectionCard;
